import { Data, DotLottieReact } from '@lottiefiles/dotlottie-react'
import classNames from 'classnames'
import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_AVATAR_URL } from '../../constats'
import styles from './Avatar.module.scss'

interface AvatarProps {
  srcAvatar: string
  srcEffect: string
  size?: number
  className?: string
  eager?: boolean
}

export const getExtension = (value: string): string | null => {
  const match = value.match(/\.([a-z0-9]+)$/i)
  return match ? match[1] : null
}

const lottieDataCache = new Map<string, Promise<ArrayBuffer>>()

function fetchLottieData(url: string): Promise<ArrayBuffer> {
  if (!lottieDataCache.has(url)) {
    lottieDataCache.set(
      url,
      fetch(url).then((res) => res.arrayBuffer()),
    )
  }
  return lottieDataCache.get(url)!
}

const ItemCardContentLottie = memo(function ItemCardContentLottie({
  imgUrl,
}: {
  imgUrl: string
}) {
  const [data, setData] = useState<Data>()

  useEffect(() => {
    let cancelled = false
    fetchLottieData(imgUrl).then((buffer) => {
      if (!cancelled) {
        // Clone the buffer — DotLottieReact may detach the original
        setData(buffer.slice(0))
      }
    })
    return () => {
      cancelled = true
    }
  }, [imgUrl])

  if (!data) {
    return null
  }

  return <DotLottieReact data={data} loop autoplay />
})

const AvatarContent = ({
  imgUrl,
  className,
  size,
}: {
  imgUrl: string
  className?: string
  size?: number
}) => {
  const extension = useMemo(() => getExtension(imgUrl), [imgUrl])

  if (extension === 'lottie') {
    return <ItemCardContentLottie imgUrl={imgUrl} />
  }

  return (
    <img
      src={imgUrl || DEFAULT_AVATAR_URL}
      className={classNames(className, styles.avatarImage)}
      width={size}
      height={size}
    />
  )
}


const LazyAvatarEffect = memo(function LazyAvatarEffect({
  imgUrl,
  size,
  className,
  eager = true,
}: {
  imgUrl: string
  size: number
  className?: string
  eager?: boolean
}) {
  const [isVisible, setIsVisible] = useState(eager)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (eager) return
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { rootMargin: '100%' },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [eager])

  const effectStyle = {
    width: `${size}rem`,
    height: `${size}rem`,
  }

  return (
    <div ref={ref} className={styles.effectContainer} style={effectStyle}>
      {isVisible && (
        <AvatarContent imgUrl={imgUrl} size={size} className={className} />
      )}
    </div>
  )
})

export const Avatar = ({
  srcAvatar,
  srcEffect,
  size = 40,
  className,
  eager = true,
}: AvatarProps) => {
  const avatarStyle = {
    width: `${size}rem`,
    height: `${size}rem`,
  }

  const effectSize = size * 2

  return (
    <div className={classNames(styles.container, className)}>
      {srcAvatar && (
        <div className={styles.avatarContainer} style={avatarStyle}>
          <AvatarContent imgUrl={srcAvatar} size={size} />
        </div>
      )}
      {srcEffect && (
        <LazyAvatarEffect
          imgUrl={srcEffect}
          size={effectSize}
          className={styles.effectAnimation}
          eager={eager}
        />
      )}
    </div>
  )
}
